import { apiService } from '@/services'
import { GeneratePermissions } from './permissions'
import type { Permissions } from './permissions'
import type { Dismissal, LoginUser, UserInfo, UserRole, UserTokens } from '@/services/organization-user/types'
import type { CSMDetails, Organization } from '@/services/organization/types'
import type { ContentChannel } from '@/services/channel/types'
import { Commit } from 'vuex'
import { AMERICA_NEW_YORK, TRUVID_TIME_ZONES } from '../services/constants'
import { OrganizationTypes } from '@/services/organization-user/constants'

/* eslint-disable camelcase */
export interface User {
  // eslint-disable-next-line camelcase
  notifications_key: string
  tokens: UserTokens
  user: UserInfo
  organization: Organization
  organizationType: OrganizationTypes[]
  roles: UserRole[]
  features: string[]
  playerLoaded: boolean
  organization_id: number
  tokens_updated: number
  permissions: Permissions
  channel_info: ContentChannel | object
  users: User[]
  activeUserIndex: number
  mode: string
  isRootUserAgency: boolean
  isLoggingFromAuth: false
  onboardingCurrentStep: number
  dismissals: Dismissal[]
}

function getDefaultUserInfo(): UserInfo {
  return {
    id: -1,
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    country_id: 0,
    language_id: 0,
    timezone: `${TRUVID_TIME_ZONES[AMERICA_NEW_YORK].name}`,
    is_active: true,
    organization_id: -1,
    created_at: '',
    last_login: '0',
    color: 'rgb(256,256,256)'
  }
}

function getDefaultUserTokens(): UserTokens {
  return {
    access_token: '',
    refresh_token: '',
    expires: 0,
    refresh_expires: 0
  }
}

export const user = {
  namespaced: true,
  state: () => ({
    users: [], // Array to store user profiles
    isRootUserAgency: false, // Boolean to know what to set in UI
    mode: 'user', // String to check which account we are
    activeUserIndex: -1, // Current active user picked from user array
    notifications_key: '',
    tokens: getDefaultUserTokens(),
    user: getDefaultUserInfo(),
    organization: {},
    roles: [],
    features: [],
    organizationType: [],
    playerLoaded: false,
    tokens_updated: 0,
    permissions: {},
    channel_info: null,
    isLoggingFromAuth: false,
    dismissals: []
  }),
  mutations: {
    updateUserData(state, userData) {
      state.user = userData.user
      state.tokens = userData.tokens
      state.roles = userData.roles
      state.features = userData.features ?? []
      state.permissions = GeneratePermissions(userData.roles)
      state.notifications_key = userData.notifications_key
      state.dismissals = userData.user?.dismissals ?? []
      state.tokens_updated = new Date().getTime()
      if (userData.organization) {
        state.organization = userData.organization
        state.organizationType = userData.organizationType ?? []
        if (userData.channel_info) {
          state.channel_info = userData.channel_info
        }
      }
    },
    updateUserFeatures(state, features: string[]) {
      state.features = features ?? []
    },
    setDismissals(state, dismissals: Dismissal[]) {
      state.dismissals = dismissals ?? []
    },
    setUserColor(state, color: string) {
      state.user.color = color
    },
    addUserToUsersList(state, userData) {
      state.users.push(userData)
    },

    setUserChannelHandle(state, handle: string) {
      state.channel_info.handle = handle
    },

    setIsRootUserAgency(state, res: boolean) {
      state.isRootUserAgency = res
    },

    setMode(state, res: string) {
      state.mode = res
    },

    removeRemoteUsers(state: User) {
      state.users = state.users.slice(0, 1)
      state.channel_info = {}
    },

    setNewTokens(state: User, tokens: UserTokens) {
      state.tokens = tokens
      state.tokens_updated = new Date().getTime()
    },

    setUserInfo(state: User, userInfo: UserInfo) {
      state.user.first_name = userInfo.first_name
      state.user.last_name = userInfo.last_name
      state.user.email = userInfo.email
      state.user.phone_number = userInfo.phone_number
      state.user.country_id = userInfo.country_id
      state.user.language_id = userInfo.language_id
      state.user.timezone = userInfo.timezone
    },

    logout(state: User) {
      state.tokens = getDefaultUserTokens()
      state.user = getDefaultUserInfo()
      state.roles = []
      state.features = []
      state.organizationType = []
      state.organization = {} as Organization
      state.tokens_updated = new Date().getTime()
      state.notifications_key = ''
      state.permissions = GeneratePermissions([])
      state.users = []
      state.activeUserIndex = 0
      state.mode = 'user'
      state.isRootUserAgency = false
      state.channel_info = {}
      state.isLoggingFromAuth = false
      state.dismissals = []
    },
    setPlayerLoaded(state: { playerLoaded: boolean }, playerLoadedState: boolean) {
      state.playerLoaded = playerLoadedState
    },
    setOrgName(state: User, name: string) {
      state.organization.name = name
    },
    setOrgProfileImage(state: User, profileImage: string) {
      state.organization.profile_image = profileImage
    },
    setUserProfileImage(state: User, profileImage: string) {
      state.user.user_profile_image = profileImage
    },
    setOrg(state: User, org: Organization) {
      state.organization = org
      const orgTypes = [
        org.is_content_owner && OrganizationTypes.Content,
        org.is_publisher && OrganizationTypes.Publisher,
        org.is_agency && OrganizationTypes.Agency
      ].filter(Boolean) as OrganizationTypes[]
      state.organizationType = orgTypes
      state.channel_info = { handle: org.channel_handle }
      if (state.activeUserIndex >= 0 && state.users[state.activeUserIndex]) {
        state.users[state.activeUserIndex].organization = org
        state.users[state.activeUserIndex].organizationType = orgTypes
        state.users[state.activeUserIndex].channel_info = { handle: org.channel_handle }
      }
    },
    setChannelInfo(state: User, info: ContentChannel) {
      state.channel_info = info
    },
    setActiveUserIndex(state, index) {
      state.activeUserIndex = index
    },
    setIsLoginFromAuth(state, isFromAuth: boolean) {
      state.isLoggingFromAuth = isFromAuth
    },
    setUserOnboardingCompletedStep(state, step: number) {
      state.user.console_init_org_wizard_step = step
    },
    setUserOnboardingCurrentStep(state, step: number) {
      state.user.onboardingCurrentStep = step
    }
  },
  actions: {
    switchUser(context, index) {
      context.commit('setActiveUser', index)
    },
    async extendOrgData(context) {
      try {
        const org = await apiService.organization.getOrganization(context.state.user.organization_id)
        context.commit('setOrg', org)
      } catch (error) {
        // Handle error if necessary
        console.error('Error while extending organization data:', error)
      }
    },
    setOrg({ commit }: { commit: Commit; state: User }, { orgData }: { orgData: Organization }): void {
      commit('setOrg', orgData)
    },
    setAccessToken({ commit, state }: { commit: Commit; state: User }, { userData }: { userData: LoginUser }): void {
      if (state.activeUserIndex !== -1) {
        commit('updateUserData', userData)
      }
    },
    setActiveUser({ commit, state }: { commit: Commit; state: User }, index: number) {
      commit('setActiveUserIndex', index)
      const activeUserData = state.users[index]
      commit('updateUserData', activeUserData)
    },
    addNewUser({ commit, state }: { commit: Commit; state: User }, userData: LoginUser) {
      commit('addUserToUsersList', userData)
      commit('updateUserData', userData)
      commit('setActiveUserIndex', state.users.length - 1)
    },
    updateUserProfileImage({ commit }: { commit: Commit }, profileImage: string) {
      commit('setUserProfileImage', profileImage)
    },
    updateUserFeatures({ commit }: { commit: Commit }, features: string[]) {
      commit('updateUserFeatures', features)
    },
    setOrgProfileImage({ commit }: { commit: Commit }, profileImage: string) {
      commit('setOrgProfileImage', profileImage)
      commit('setUserProfileImage', profileImage)
    },
    setUserOnboardingCompletedStep({ commit }: { commit: Commit }, step: number) {
      commit('setUserOnboardingCompletedStep', step)
    },
    setUserOnboardingCurrentStep({ commit }: { commit: Commit }, step: number) {
      commit('setUserOnboardingCurrentStep', step)
    }
  },
  getters: {
    isRole: (state) => (role) => {
      return state.roles
        ?.map((r) => {
          return r.name
        })
        ?.includes(role)
    },
    getRoles: (state) => () => {
      return state.roles.map((r) => {
        return r
      })
    },
    getOrganizationType: (state) => {
      return state.organizationType
    },
    getOrganizationCSM: (state): (() => CSMDetails | null) => {
      return () => state.organization?.csm || null
    },
    getId: (state) => () => {
      return state.user.id
    },
    getOrganizationId: (state) => () => {
      return state.user.organization_id
    },
    getOrgProfileImage: (state) => () => {
      return state.organization.profile_image
    },
    getUserProfileImage: (state) => () => {
      return state.user.user_profile_image
    },
    getUserTimezone: (state) => () => {
      return state.user.timezone
    },
    getUserFullName: (state) => () => {
      return {
        first_name: state.user.first_name,
        last_name: state.user.last_name
      }
    },
    getConsoleInitOrgWizardStep: (state) => () => {
      return state.user.console_init_org_wizard_step
    },
    getUserOnboardingCurrentStep: (state) => () => {
      return state.user.onboardingCurrentStep
    },
    getOrganizationCategories: (state) => () => {
      return state.organization?.categories || []
    },
    getOrganizationInterests: (state) => () => {
      return state.organization?.interests || []
    },
    getIsLoggingFromAuth: (state) => () => {
      return state.isLoggingFromAuth
    },
    isFeatureDismissed: (state) => (featureKey: string) => {
      return state.dismissals?.some((dismissal: Dismissal) => dismissal.feature_key === featureKey && dismissal.completed) ?? false
    }
  }
}
